import { ApiError, apiGetOrNull, apiRequest } from '@/domains/api';
import type { IPage, SubjectKind } from './oversight-contracts';
import type {
  IAdminAccountLink,
  IAdminActivityRow,
  IAdminDisclosureGroup,
  IAdminFirmRow,
  IAdminParticipantDetail,
  IAdminParticipantHub,
  IAdminParticipantListItem,
  IAdminParticipantPendingLookup,
  IAdminPersonRow,
  IAdminRegistrationRow,
  IAdminRoster,
  IAdminSalesforceObject,
  IAdminSalesforceRecord,
  IAdminSalesforceTask,
  IAdminTeamRouting,
  IAdminTeamRow,
  FirmFacetField,
  IFirmsQuery,
  IParticipantNumber,
  IPeopleCounts,
  IPeopleQuery,
  IRecordFacets,
  IRegistrationCounts,
  IRegistrationQuery,
  IRosterCard,
  IStampedIdentifier,
  ITeamRoutingDecision,
  ITeamCounts,
  ITeamsQuery,
  RecordType,
  RegistrationFacetField,
  TeamFacetField
} from './participant-contracts';

/**
 * survey-api's participant surface — the advisor profile (SR-213 block 8), the registration
 * queue (A6), the account links (block 6), the person hub (SR-215), a team's routing and
 * archive (SR-213's Teams block), the disclosures (SR-216) and the photo (SR-222's block 8): under
 * `/api/v1/admin/participants/{id}`, the hub and its activity, the disclosures and the archived
 * Salesforce tasks, the Salesforce archive (`…/salesforce-records`, SR-222), a team's or firm's
 * roster and Veritant's decision about a slot (`…/roster`, SR-222), the registry refresh, the
 * identifier verification, the link and its undo, Veritant's routing decision, the archive and the
 * restore, the photo set and removed; `GET /api/v1/admin/registrations`; the People list,
 * `GET /api/v1/admin/people`, and a roster row's card under it (SR-414); and the Teams and Firms
 * lists, `GET /api/v1/admin/teams` and `…/firms` (SR-414). The sole reader of all five prefixes,
 * bar the participant notes below. survey-api's participant list, `GET /api/v1/admin/participants`,
 * is read by a ranking's Insert alone (SR-231): it lists every participant, a team or firm among
 * them, which People — persons only since SR-414 — cannot find.
 *
 * `/admin/people`, `/admin/teams` and `/admin/firms` are read here rather than from a module of
 * their own although survey-api gave them prefixes and files of their own: they are the same
 * surface — identity — under the same policy, `responses.browse`, and their rows are participant
 * rows grouped by person, team or firm. A second module would be a second reader of one surface,
 * which is what this repo's one-reader rule forbids.
 *
 * Its own module rather than more of `oversight-api.ts`, for survey-api's reason and
 * `accounts-api.ts`'s: this reads **identity**, not responses. It keeps that surface's policies
 * — reads under `responses.browse`, writes under `responses.annotate`, both staff and admin —
 * which is what separates it from `accounts-api.ts`, whose reads are admin alone. The account
 * links are the exception survey-api makes on purpose: they carry `accounts.manage`, seat
 * creation's policy, because deciding which login acts for a person is the other half of giving
 * somebody a seat. It is staff and admin too, so the hub's audience is unchanged.
 *
 * Two participant calls deliberately stay in `oversight-api.ts`: the participant note write,
 * beside `participantNotes` (survey-api mapped that POST beside its GET — one pair of routes
 * for one resource), and `participantId` on `listResponses`, survey-api's filter on that list —
 * that list narrowed rather than a new read, and passed by nothing here since the hub carries
 * the household's responses in its own document.
 *
 * Nothing here groups, filters or judges what comes back. A number's registry is the hub's to
 * derive from `idType`; `q` is sent as typed; and the refresh verifies nothing and pins nothing
 * — its 202 is a job enqueued, whose answer arrives on a later read of the hub.
 */

const BASE = '/api/v1/admin';

/**
 * A participant's path segment. `encodeURIComponent` keeps anything the caller passes inside
 * one segment — but not a bare `.` or `..`, which it leaves alone and the URL parser behind
 * `fetch` then removes: `participants/.` would reach the **list** route and render a page of
 * rows as one participant, and `participants/./hub` would ask for a participant named `hub`.
 * Callers of `participantPath` hold a server-issued GUID; the hub route is the one that holds
 * whatever the address bar said, and `getHub` answers those values itself.
 */
function participantPath(participantId: string): string {
  return `${BASE}/participants/${encodeURIComponent(participantId)}`;
}

/** survey-api's ids, as it issues them — `{invitationId:guid}` on both invitation routes. */
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** An invitation's path, both ids refused unsent unless each is a GUID — the hub hands over ids
 *  survey-api issued, and anything else spliced in would reach another route. */
function invitationPath(participantId: string, invitationId: string): string {
  if (!GUID.test(participantId) || !GUID.test(invitationId)) {
    throw new Error(`"${participantId}" / "${invitationId}" does not name an invitation.`);
  }
  return `${participantPath(participantId)}/invitations/${invitationId}`;
}

/** Values that could only reach a different route — see `participantPath`. */
const NOT_A_SEGMENT = new Set(['', '.', '..']);

/** A query string — `?…` — or nothing, for parameters none of which was given. */
function rendered(params: URLSearchParams): string {
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * The parameters People, Teams and Firms share, each only where given. `sort` goes as `key` or
 * `key:desc`; `createdSince` as the instant it was given. Nothing is checked first: an unknown
 * value, code or sort is survey-api's 400 `invalid-query`, and its problems are what a screen
 * shows.
 */
function listParams(query: {
  q?: string;
  cycle?: string;
  verification?: string;
  registry?: string;
  account?: string;
  disclosures?: string;
  teamRouting?: string;
  teamRoutingSource?: string;
  teamLeadShared?: boolean;
  createdSince?: string;
  sort?: { key: string; descending: boolean };
  page?: number;
  pageSize?: number;
}): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q !== undefined && query.q !== '') params.append('q', query.q);
  if (query.cycle !== undefined) params.append('cycle', query.cycle);
  if (query.verification !== undefined) params.append('verification', query.verification);
  if (query.registry !== undefined) params.append('registry', query.registry);
  if (query.account !== undefined) params.append('account', query.account);
  if (query.disclosures !== undefined) params.append('disclosures', query.disclosures);
  if (query.teamRouting !== undefined) params.append('teamRouting', query.teamRouting);
  if (query.teamRoutingSource !== undefined) {
    params.append('teamRoutingSource', query.teamRoutingSource);
  }
  if (query.teamLeadShared !== undefined) {
    params.append('teamLeadShared', String(query.teamLeadShared));
  }
  if (query.createdSince !== undefined) params.append('createdSince', query.createdSince);
  if (query.sort !== undefined) {
    params.append('sort', `${query.sort.key}${query.sort.descending ? ':desc' : ''}`);
  }
  if (query.page !== undefined) params.append('page', String(query.page));
  if (query.pageSize !== undefined) params.append('pageSize', String(query.pageSize));
  return params;
}

/** The People query's parameters — the shared ones, the surveys, `include` and the card. */
function peopleParams(query: IPeopleQuery): URLSearchParams {
  const params = listParams(query);
  for (const survey of query.survey ?? []) params.append('survey', survey);
  if (query.include !== undefined) params.append('include', query.include);
  if (query.card !== undefined) params.append('card', query.card);
  return params;
}

/** The Teams query's parameters — the shared ones, `roster`, and the card. */
function teamParams(query: ITeamsQuery): URLSearchParams {
  const params = listParams(query);
  if (query.roster !== undefined) params.append('roster', query.roster);
  if (query.card !== undefined) params.append('card', query.card);
  return params;
}

/** The Firms query's parameters — the shared ones, `roster`, `secAdv` and the record type. */
function firmParams(query: IFirmsQuery): URLSearchParams {
  const params = listParams(query);
  if (query.roster !== undefined) params.append('roster', query.roster);
  if (query.secAdv !== undefined) params.append('secAdv', query.secAdv);
  if (query.recordType !== undefined) params.append('recordType', query.recordType);
  return params;
}

/**
 * A facets read's query over a list's parameters: the field, its own filter set aside, no sort or
 * window — and, as on the register's facets, the list's search as `search`, since `q` there is the
 * value prefix.
 */
function facetQuery(field: string, params: URLSearchParams, prefix: string): string {
  for (const name of [field, 'sort', 'page', 'pageSize']) params.delete(name);
  const search = params.get('q');
  params.delete('q');
  if (search !== null) params.set('search', search);
  params.set('field', field);
  if (prefix !== '') params.set('q', prefix);
  return rendered(params);
}

/**
 * A roster card's answer, or a thrown failure where a 2xx carried none — `slots` the least the
 * page is drawn by, `rosterOf`'s rule and for its reason.
 */
function rosterCardOf(answer: unknown): IRosterCard {
  const card = answer as Nullable<Partial<IRosterCard>>;
  if (!Array.isArray(card?.slots)) throw new Error('The answer held no roster card');
  return card as IRosterCard;
}

/** A paged read's query — `?page=…&pageSize=…`, each only where given — or nothing. */
function pageQuery(
  query: { page?: number; pageSize?: number },
  params: URLSearchParams = new URLSearchParams()
): string {
  if (query.page !== undefined) params.append('page', String(query.page));
  if (query.pageSize !== undefined) params.append('pageSize', String(query.pageSize));
  return rendered(params);
}

/**
 * A roster's answer, or a thrown failure where a 2xx carried none — both lists there, the least
 * the section and the Overview fact are drawn by. A body of another shape (an empty one reads as
 * `null`) would otherwise throw from render, and the app has no error boundary: the whole hub
 * would go blank. Thrown, it is a failed read said like any other, and on a write an answer that
 * never arrived — re-read and asked to be checked. What is inside the lists is not judged.
 */
function rosterOf(answer: unknown): IAdminRoster {
  const roster = answer as Nullable<Partial<IAdminRoster>>;
  if (!Array.isArray(roster?.members) || !Array.isArray(roster?.memberships)) {
    throw new Error('The answer held no roster');
  }
  return roster as IAdminRoster;
}

export const participantsApi = {
  /**
   * The household in one read (SR-215, person hub §4) — the opened participant's card (the
   * detail document the participant card page used to read alone), every other participant an
   * account of it acts for, and the household's responses, notes and pending lookups, all
   * unpaged — or `null` when there is no such participant: a 404 through `apiGetOrNull`, for an
   * unknown id and for one that is not a GUID alike (the route's `guid` constraint falls through
   * to 404). Every other status still throws, so a read that failed cannot be mistaken for
   * nobody being there.
   *
   * An id that could not be sent as one segment is answered here with that same `null`: it is
   * what the server would say, and sending it would ask a different route. The hub and the card
   * (`getParticipant`) are the routes of this module that hold whatever the address bar said.
   *
   * Every read of a caller holding `internal_fields.read` asks `?include=internal` — the poll's
   * and every re-read's (the F2 plan's R10) — so the hub carries each responding member's
   * staff-field record as `internal`. Any other caller's asks nothing: survey-api answers the
   * include 403 to a caller without that permission, the **whole** hub with it (SR-228), where
   * the hub's audience is `responses.browse`. An API without it ignores the parameter and leaves
   * the member out.
   */
  getHub(
    participantId: string,
    withInternal: boolean,
    signal?: AbortSignal
  ): Promise<Nullable<IAdminParticipantHub>> {
    if (NOT_A_SEGMENT.has(participantId)) return Promise.resolve(null);
    return apiGetOrNull<IAdminParticipantHub>(
      `${participantPath(participantId)}/hub${withInternal ? '?include=internal' : ''}`,
      { signal }
    );
  },

  /**
   * One participant's card, alone — or `null` when there is no such participant, as `getHub`
   * answers it. Read by a number check's pane (SR-487 ruling 15): survey-api carries a check on
   * its participant's card (`checks`, every status) and has no route for one check, and the card
   * is the smallest read that holds it.
   */
  getParticipant(
    participantId: string,
    signal?: AbortSignal
  ): Promise<Nullable<IAdminParticipantDetail>> {
    if (NOT_A_SEGMENT.has(participantId)) return Promise.resolve(null);
    return apiGetOrNull<IAdminParticipantDetail>(participantPath(participantId), { signal });
  },

  /**
   * The household's activity, newest first (person hub §8). Unlike the hub it throws for an
   * unknown participant — its caller has already read the hub, so a 404 here is the household
   * disappearing between two reads, a failure to say rather than an empty feed. A bad page is
   * 400 `invalid-query`, checked by survey-api before the participant.
   */
  listActivity(
    participantId: string,
    query: { page?: number; pageSize?: number; group?: readonly string[] } = {},
    signal?: AbortSignal
  ): Promise<IPage<IAdminActivityRow>> {
    // `group` narrows the feed to survey-api's activity groups (SR-407), repeated; none is all.
    const params = new URLSearchParams();
    for (const group of query.group ?? []) params.append('group', group);
    return apiRequest<IPage<IAdminActivityRow>>(
      `${participantPath(participantId)}/activity${pageQuery(query, params)}`,
      { signal }
    );
  },

  /**
   * The disclosures of the participant's CRD set (SR-216, disclosures spec §6.3) — one group per
   * CRD, in survey-api's order, `[]` for an empty set. Unpaged: a group is bounded by FINRA's
   * list for one person. It throws for an unknown participant, as `listActivity` does and for
   * its reason: its caller has read the hub, so a 404 is the household going between two reads.
   */
  listDisclosures(participantId: string, signal?: AbortSignal): Promise<IAdminDisclosureGroup[]> {
    return apiRequest<IAdminDisclosureGroup[]>(`${participantPath(participantId)}/disclosures`, {
      signal
    });
  },

  /**
   * The archived Salesforce tasks filed under the participant's Salesforce records (disclosures
   * spec §6.5), newest first — read-only history, written by the migration alone. Throws on a
   * 404 for `listActivity`'s reason; a bad page is 400 `invalid-query`, checked before the
   * participant.
   */
  listSalesforceTasks(
    participantId: string,
    query: { page?: number; pageSize?: number } = {},
    signal?: AbortSignal
  ): Promise<IPage<IAdminSalesforceTask>> {
    return apiRequest<IPage<IAdminSalesforceTask>>(
      `${participantPath(participantId)}/salesforce-tasks${pageQuery(query)}`,
      { signal }
    );
  },

  /**
   * The archived Salesforce objects that reach the participant (SR-222, Salesforce archive spec
   * §5) — `…/salesforce-records` **without** `sobject`: a bare array, in survey-api's order, an
   * object with no row left out.
   */
  listSalesforceObjects(
    participantId: string,
    signal?: AbortSignal
  ): Promise<IAdminSalesforceObject[]> {
    return apiRequest<IAdminSalesforceObject[]>(
      `${participantPath(participantId)}/salesforce-records`,
      { signal }
    );
  },

  /**
   * One page of an archived object's records for the participant — the same route **with**
   * `sobject`, the wire's other shape. An `sobject` the archive holds no describe of is 400
   * `invalid-query` at `sobject`, checked before the participant; one the archive holds that
   * does not reach this participant is an empty page.
   */
  listSalesforceRecords(
    participantId: string,
    sobject: string,
    query: { page?: number; pageSize?: number } = {},
    signal?: AbortSignal
  ): Promise<IPage<IAdminSalesforceRecord>> {
    const path = `${participantPath(participantId)}/salesforce-records`;
    return apiRequest<IPage<IAdminSalesforceRecord>>(
      `${path}${pageQuery(query, new URLSearchParams({ sobject }))}`,
      { signal }
    );
  },

  /**
   * The participant list (`responses.browse`): `q` as typed, `kind` and `recordType` repeated,
   * survey-api's order — ranked with `q` since SR-414, roster members found — and paging; each row
   * carries why it matched. The ranking Insert's search.
   */
  listParticipants(
    query: {
      q: string;
      kind?: readonly SubjectKind[];
      recordType?: readonly RecordType[];
      pageSize?: number;
    },
    signal?: AbortSignal
  ): Promise<IPage<IAdminParticipantListItem>> {
    const params = new URLSearchParams({ q: query.q });
    for (const kind of query.kind ?? []) params.append('kind', kind);
    for (const type of query.recordType ?? []) params.append('recordType', type);
    if (query.pageSize !== undefined) params.append('pageSize', String(query.pageSize));
    return apiRequest<IPage<IAdminParticipantListItem>>(`${BASE}/participants?${params}`, {
      signal
    });
  },

  /**
   * The People list (person hub §6; record search §3.1) — one row per person, persons only.
   * `survey` is repeated once per code under that plain name (survey-api hears `survey[]` too);
   * `include` is `roster` or absent, and adds the unregistered members as roster rows.
   */
  listPeople(query: IPeopleQuery = {}, signal?: AbortSignal): Promise<IPage<IAdminPersonRow>> {
    return apiRequest<IPage<IAdminPersonRow>>(`${BASE}/people${rendered(peopleParams(query))}`, {
      signal
    });
  },

  /** Each People card's count under the list's search and filters (SR-514) — the card, the sort
   *  and the page are not sent: a card's count never moves with them. */
  peopleCounts(query: IPeopleQuery, signal?: AbortSignal): Promise<IPeopleCounts> {
    const params = peopleParams({
      ...query,
      card: undefined,
      sort: undefined,
      page: undefined,
      pageSize: undefined
    });
    return apiRequest<IPeopleCounts>(`${BASE}/people/counts${rendered(params)}`, { signal });
  },

  /**
   * A roster row's card (record search §3.6) — or `null` once nobody unregistered answers to the
   * key (a bodiless 404: the member registered, or Veritant linked them), the page's own state
   * rather than a failure. **Only the 404 is `null`**: every other status throws, and a 2xx with no
   * slots in it — an empty body included, which the client reads as `null` — is a failed read,
   * never "they have registered" (gate 2).
   *
   * **The key is encoded per `/`-separated piece, the slash kept literal** (the owner's Q5): an
   * email key's local part may hold a `/`, survey-api maps the route as a catch-all for it (the
   * user card's `by-email` precedent), and a catch-all is matched against the decoded path —
   * `%2F` would never reach it. A piece that is `''`, `.` or `..` is refused unsent — thrown, so
   * the page says it could not read the card rather than that its member registered: the URL
   * parser behind `fetch` removes dot segments, their percent-encoded spellings too, so such a key
   * would leave here as a call to another route. No key survey-api mints holds one.
   */
  async getRosterCard(rosterKey: string, signal?: AbortSignal): Promise<Nullable<IRosterCard>> {
    const pieces = rosterKey.split('/');
    if (pieces.some((piece) => NOT_A_SEGMENT.has(piece))) {
      throw new Error('This address names no roster card');
    }
    const addressed = pieces.map(encodeURIComponent).join('/');
    try {
      return rosterCardOf(
        await apiRequest<unknown>(`${BASE}/people/roster/${addressed}`, { signal })
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

  /**
   * The Teams list (record search §3.2) — one row per team, archived ones included. People's
   * filters read over the team itself, plus its three team filters and `roster`.
   */
  listTeams(query: ITeamsQuery = {}, signal?: AbortSignal): Promise<IPage<IAdminTeamRow>> {
    return apiRequest<IPage<IAdminTeamRow>>(`${BASE}/teams${rendered(teamParams(query))}`, {
      signal
    });
  },

  /** Each Teams card's count under the list's search and filters (SR-514) — the card, the sort
   *  and the page are not sent: a card's count never moves with them. */
  teamCounts(query: ITeamsQuery, signal?: AbortSignal): Promise<ITeamCounts> {
    const params = teamParams({
      ...query,
      card: undefined,
      sort: undefined,
      page: undefined,
      pageSize: undefined
    });
    return apiRequest<ITeamCounts>(`${BASE}/teams/counts${rendered(params)}`, { signal });
  },

  /** A Teams column's values among the teams the other filters keep, counted (SR-514). */
  teamFacets(
    field: TeamFacetField,
    query: ITeamsQuery,
    prefix: string,
    signal?: AbortSignal
  ): Promise<IRecordFacets> {
    return apiRequest<IRecordFacets>(
      `${BASE}/teams/facets${facetQuery(field, teamParams(query), prefix)}`,
      { signal }
    );
  },

  /**
   * The Firms list (record search §3.3) — one row per firm or org. People's filters read over
   * the firm itself, plus `roster` and `secAdv`.
   */
  listFirms(query: IFirmsQuery = {}, signal?: AbortSignal): Promise<IPage<IAdminFirmRow>> {
    return apiRequest<IPage<IAdminFirmRow>>(`${BASE}/firms${rendered(firmParams(query))}`, {
      signal
    });
  },

  /** A Firms column's values among the firms the other filters keep, counted (SR-514). */
  firmFacets(
    field: FirmFacetField,
    query: IFirmsQuery,
    prefix: string,
    signal?: AbortSignal
  ): Promise<IRecordFacets> {
    return apiRequest<IRecordFacets>(
      `${BASE}/firms/facets${facetQuery(field, firmParams(query), prefix)}`,
      { signal }
    );
  },

  /**
   * Ask the registry about a number again (E3) — 202 with the pending lookup.
   *
   * `CRD`, `ORG_CRD` and `TEAM_LEAD_CRD` are refreshed (BrokerCheck) — a lead CRD asked as a
   * `CRD`, so its answer names `CRD` (`registries.ts`' `ASKED_AS_OF_ID_TYPE`); a type no refresh
   * covers is 422 `invalid-action` on `idType`, a number the participant does not hold 404, a
   * missing half 400. A press while a lookup of the same key is queued or running for this
   * participant answers **that** one and enqueues nothing — survey-api finds it by
   * `(participantId, factKey)`, so on a participant holding two CRDs it may be about the other
   * number, which the answer's `idType` and `value` say. It verifies nothing and pins nothing.
   */
  requestRegistryLookup(
    participantId: string,
    number: IParticipantNumber
  ): Promise<IAdminParticipantPendingLookup> {
    return apiRequest<IAdminParticipantPendingLookup>(
      `${participantPath(participantId)}/registry-lookups`,
      { method: 'POST', body: number }
    );
  },

  /**
   * Stamp a number verified by hand (A6) — 200 with the row. It only ever sets: there is no
   * route that clears `verified_at`, and a number already stamped answers with the stamp it
   * has. 404 for a number the participant does not hold.
   */
  verifyIdentifier(
    participantId: string,
    number: IParticipantNumber
  ): Promise<IStampedIdentifier> {
    return apiRequest<IStampedIdentifier>(
      `${participantPath(participantId)}/identifier-verifications`,
      { method: 'POST', body: number }
    );
  },

  /**
   * Link the account at an address to this participant (block 6, F3) — 201 with the link.
   *
   * By address, trimmed and matched in any case by survey-api. Four refusals, told apart by
   * status and `type`, never by `title`: **422 `invalid-action`** naming `email` — no account
   * has that address; **409 `participant-has-account`** — some account already acts for this
   * participant, whichever address was typed, because a second one would be delegation; **404**
   * — no such participant; **400 `invalid-body`** — a blank address. The link mails nobody and
   * revokes nothing: access is read per request.
   *
   * `relation` (SR-487, PFR §8): absent or `self` is the person's own account; `assistant` links
   * an account to a person whatever `self` links it has — 400 on `relation` for a team or a firm,
   * and 409 `already-linked` for an account linked to it other than as `member`.
   */
  linkAccount(
    participantId: string,
    email: string,
    relation?: 'self' | 'assistant'
  ): Promise<IAdminAccountLink> {
    return apiRequest<IAdminAccountLink>(`${participantPath(participantId)}/account-links`, {
      method: 'POST',
      body: relation === undefined ? { email } : { email, relation }
    });
  },

  /**
   * The undo — 204, or **404** when that account does not act for this participant (any more).
   * Any link, whoever made it: a registration's, the migration's or a member of staff's, since a
   * wrong one exposes a person's answers to somebody else. No session is revoked; the account's
   * next request is simply refused the participant. Removing the last link leaves the
   * participant with no account, which the link above then fixes.
   */
  async unlinkAccount(participantId: string, accountId: string): Promise<void> {
    await apiRequest<void>(
      `${participantPath(participantId)}/account-links/${encodeURIComponent(accountId)}`,
      { method: 'DELETE' }
    );
  },

  /**
   * Mails a pending point-of-contact invitation again (SR-416, survey-api's B5-16) — **202**, no
   * body and no `deliverTo`: survey-api's worker mints a new token (the last link dies) and moves
   * the expiry fourteen days on, the status unchanged — a `pending` row past its expiry is revived.
   * **404** (bodiless) for an invitation not under this participant, **409
   * `invitation-not-pending`** for any other status. `accounts.manage`. An id that is no GUID is
   * refused before the request: it is spliced into the path.
   */
  async resendInvitation(participantId: string, invitationId: string): Promise<void> {
    await apiRequest<void>(`${invitationPath(participantId, invitationId)}/resend`, {
      method: 'POST'
    });
  },

  /**
   * Revokes a pending invitation — **204**, the row `revoked`, its link dead, no mail. The same
   * 404 and 409 `invitation-not-pending` as the resend, and the same policy. Nothing creates an
   * invitation but a submitted response naming the address.
   */
  async revokeInvitation(participantId: string, invitationId: string): Promise<void> {
    await apiRequest<void>(invitationPath(participantId, invitationId), { method: 'DELETE' });
  },

  /**
   * Veritant's decision about a team's routing (SR-213's Teams block, its R7) — 200 with the
   * routing as staff read it after. `outcome: null` clears a decision of Veritant's and the team
   * falls to its firm; over no such decision it writes nothing and answers the routing as it
   * stands. **404** for a participant that is not a team; **400 `invalid-body`** naming
   * `outcome` or `note` (a note over survey-api's limit). `responses.annotate`.
   */
  decideTeamRouting(
    participantId: string,
    decision: ITeamRoutingDecision
  ): Promise<IAdminTeamRouting> {
    return apiRequest<IAdminTeamRouting>(`${participantPath(participantId)}/team-routing`, {
      method: 'PUT',
      body: decision
    });
  },

  /**
   * Archive a team (its R21) — 200 with the card after. The team is offered no survey from
   * then on, and its `TEAM_LEAD_CRD` is released, kept on the stamp. Archiving an archived team
   * keeps the first stamp. **404** for a participant that is not a team. `responses.annotate`.
   */
  archiveTeam(participantId: string): Promise<IAdminParticipantDetail> {
    return apiRequest<IAdminParticipantDetail>(`${participantPath(participantId)}/archive`, {
      method: 'POST'
    });
  },

  /**
   * Restore it — 200 with the card after, the released lead CRD added back **unverified**;
   * restoring a team nobody archived changes nothing. **409 `team-lead-crd-taken`** when another
   * team holds that CRD by now, **404** for a participant that is not a team.
   */
  restoreTeam(participantId: string): Promise<IAdminParticipantDetail> {
    return apiRequest<IAdminParticipantDetail>(`${participantPath(participantId)}/archive`, {
      method: 'DELETE'
    });
  },

  /**
   * A participant's roster (SR-222, survey-api's roster design §7) — a team's or firm's named
   * slots, each resolved as it is read, and the teams and firms whose slots name a person. A
   * participant that does not exist is a bodiless **404**, an `ApiError` like every hub read's:
   * the participant is the hub's own, so it is a failure to say, never an empty roster.
   * `responses.browse`.
   */
  getRoster(participantId: string, signal?: AbortSignal): Promise<IAdminRoster> {
    return apiRequest<unknown>(`${participantPath(participantId)}/roster`, { signal }).then(
      rosterOf
    );
  },

  /**
   * Veritant's decision about who sits in a slot — 200 with the whole roster after. `member` is a
   * person's id, or `null` for nobody, and is always sent: survey-api binds `{}` as null too, and
   * an absent member must never be what decides nobody. The same decision again writes nothing.
   * **404** for no participant or no row in that slot; **422 `roster-member-not-person`** for a
   * member that is no participant or not a person, checked after the slot. `responses.annotate`.
   */
  setRosterMember(
    participantId: string,
    slot: number,
    member: Nullable<string>
  ): Promise<IAdminRoster> {
    return apiRequest<unknown>(`${participantPath(participantId)}/roster/${slot}`, {
      method: 'PUT',
      body: { participantId: member }
    }).then(rosterOf);
  },

  /** Take Veritant's decision back — 200 with the roster, the slot resolved by Salesforce's link or
   *  its CRD again; with no decision it writes nothing. **404** as `setRosterMember`'s. */
  clearRosterMember(participantId: string, slot: number): Promise<IAdminRoster> {
    return apiRequest<unknown>(`${participantPath(participantId)}/roster/${slot}`, {
      method: 'DELETE'
    }).then(rosterOf);
  },

  /**
   * Set or replace the participant's photo (SR-222's block 8) — 200 with the card after, its
   * `photoUri` the stored image's public URL. The body is the file's bytes with its own type
   * (`client.ts` sends a `Blob` as itself), not a form; survey-api names the blob by the content's
   * hash, so the same file again changes nothing. **400 `photo-empty`**, **413
   * `photo-too-large`** (over 5 MB), **415 `photo-unsupported-type`** (not a JPEG, PNG, GIF or
   * WebP, or declared as another of them), each judged before the participant; then **404**,
   * bodiless, for nobody. `responses.annotate`.
   */
  setPhoto(participantId: string, file: Blob): Promise<IAdminParticipantDetail> {
    return apiRequest<IAdminParticipantDetail>(`${participantPath(participantId)}/photo`, {
      method: 'PUT',
      body: file
    });
  },

  /** Remove it — 200 with the card after, `photoUri` null; over no photo it writes nothing.
   *  **404**, bodiless, for nobody. The stored file stays; no route brings it back. */
  clearPhoto(participantId: string): Promise<IAdminParticipantDetail> {
    return apiRequest<IAdminParticipantDetail>(`${participantPath(participantId)}/photo`, {
      method: 'DELETE'
    });
  },

  /** Registration attempts (SR-515) — newest started first unless `sort` says otherwise;
   *  `status` and `flag` repeated once per value, none meaning every status. */
  listRegistrations(
    query: IRegistrationQuery = {},
    signal?: AbortSignal
  ): Promise<IPage<IAdminRegistrationRow>> {
    return apiRequest<IPage<IAdminRegistrationRow>>(
      `${BASE}/registrations${rendered(registrationParams(query))}`,
      { signal }
    );
  },

  /** Each Registrations card's count and its signals' under the list's search and filters
   *  (SR-515) — the card, the flags, the sort and the page are not sent. */
  registrationCounts(
    query: IRegistrationQuery,
    signal?: AbortSignal
  ): Promise<IRegistrationCounts> {
    const params = registrationParams({
      ...query,
      card: undefined,
      flag: undefined,
      sort: undefined,
      page: undefined,
      pageSize: undefined
    });
    return apiRequest<IRegistrationCounts>(`${BASE}/registrations/counts${rendered(params)}`, {
      signal
    });
  },

  /** A Registrations column's values among the attempts the other filters keep, counted
   *  (SR-515) — `search` the prefix typed, the list's own `q` kept as it is. */
  registrationFacets(
    field: RegistrationFacetField,
    query: IRegistrationQuery,
    prefix: string,
    signal?: AbortSignal
  ): Promise<IRecordFacets> {
    const params = registrationParams({
      ...query,
      [field]: undefined,
      sort: undefined,
      page: undefined,
      pageSize: undefined
    });
    params.set('field', field);
    if (prefix !== '') params.set('search', prefix);
    return apiRequest<IRecordFacets>(`${BASE}/registrations/facets${rendered(params)}`, {
      signal
    });
  }
};

/** The Registrations query as parameters, each only where given; a list repeats its key. */
function registrationParams(query: IRegistrationQuery): URLSearchParams {
  const params = new URLSearchParams();
  const scalars: [string, string | number | undefined][] = [
    ['q', query.q?.trim() || undefined],
    ['card', query.card],
    ['since', query.since],
    ['survey', query.survey],
    ['step', query.step],
    ['sort', query.sort],
    ['page', query.page],
    ['pageSize', query.pageSize]
  ];
  for (const [key, value] of scalars) if (value !== undefined && value !== '') params.set(key, String(value));
  for (const flag of query.flag ?? []) params.append('flag', flag);
  for (const status of query.status ?? []) params.append('status', status);
  return params;
}
